import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { Order } from '@klareospace/pharos-lib-types';

import type { UpdateOrderRequest } from '../api';
import { useOrderUpdateErrorMessage } from './use-order-update-error-message';
import { useUpdateOrder } from './use-update-order';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseInlineOrderFieldEditOptions<TValue> {
  buildPatch: (value: TValue) => UpdateOrderRequest;
  /** Confirm is disabled unless this returns true — defaults to always valid. */
  isValid?: (value: TValue) => boolean;
  order: Order;
  /** i18n key for the success toast. */
  successKey: string;
  value: TValue;
}

export interface UseInlineOrderFieldEditReturn<TValue> {
  draft: TValue;
  handleCancel: () => void;
  /**
   * Confirms the edit. Accepts an optional `overrideValue` for callers that need to fold a
   * not-yet-committed local value (e.g. text still sitting in an input) into the submitted
   * value — `setDraft` alone can't be read synchronously afterward because `draft` is captured
   * in this closure from the last render.
   */
  handleConfirm: (overrideValue?: TValue) => void;
  isEditing: boolean;
  isPending: boolean;
  isValid: boolean;
  setDraft: (value: TValue) => void;
  startEdit: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Shared draft/confirm/cancel/revert lifecycle for the drawer's inline content edits
 * (weight, volume, handling-unit quantity, price, tags) and the carrier inline edit.
 * Mirrors PHA-270's task-assignment draft pattern (local draft + explicit confirm/cancel),
 * but adds toast feedback + revert-on-rejection per this ticket's acceptance criteria.
 */
export function useInlineOrderFieldEdit<TValue>({
  buildPatch,
  isValid = (): boolean => true,
  order,
  successKey,
  value,
}: UseInlineOrderFieldEditOptions<TValue>): UseInlineOrderFieldEditReturn<TValue> {
  const { t } = useTranslation('orders');
  const updateOrder = useUpdateOrder(order.id);
  const resolveErrorMessage = useOrderUpdateErrorMessage(order.id);

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<TValue>(value);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [isEditing, value]);

  function startEdit(): void {
    setDraft(value);
    setIsEditing(true);
  }

  function handleCancel(): void {
    setDraft(value);
    setIsEditing(false);
  }

  function handleConfirm(overrideValue?: TValue): void {
    const valueToSubmit = overrideValue ?? draft;
    if (!isValid(valueToSubmit)) return;
    const patch = buildPatch(valueToSubmit);
    if (Object.keys(patch).length === 0) {
      // A no-op patch (e.g. confirming without having changed anything) — skip the round trip,
      // the misleading success toast, and the invalidation-triggered refetch entirely.
      setIsEditing(false);
      return;
    }
    updateOrder.mutate(patch, {
      onError: (error: unknown) => {
        setDraft(value);
        toast.error(resolveErrorMessage(error));
      },
      onSuccess: () => {
        setIsEditing(false);
        toast.success(t(successKey));
      },
    });
  }

  return {
    draft,
    handleCancel,
    handleConfirm,
    isEditing,
    isPending: updateOrder.isPending,
    isValid: isValid(draft),
    setDraft,
    startEdit,
  };
}
